import { TradeInterestType } from "@/lib/interfaces and Enums/ENUMSwapTradeMessage";

// The Cube security type a swap will be classed as, derived from its legs the same way
// the API does (SecurityTypeBusinessRule.cs). Live from the ticket, so it follows edits.
// OBS and OIS need a reference rate's compounding flag, which the front end lacks: OBS
// falls into IBS and OIS into IRS (LEG_CREATION_PLAN Q1).

/* eslint-disable no-unused-vars */
export enum SwapType {
  ZCS = "ZCS",
  IBS = "IBS",
  XBS = "XBS",
  XCS = "XCS",
  IRS = "IRS",
}

export interface ISwapTypeLeg {
  interestType: TradeInterestType | string;
  currency: string;
  couponFrequency: string;
  resetFrequency?: string;
}

const ZERO_FREQUENCY = "Zero";

export function returnSwapType(leg1: ISwapTypeLeg, leg2: ISwapTypeLeg): SwapType {
  if (
    leg1.couponFrequency === ZERO_FREQUENCY ||
    leg2.couponFrequency === ZERO_FREQUENCY
  ) {
    return SwapType.ZCS;
  }
  const isCrossCurrency = leg1.currency !== leg2.currency;
  const isBothFloating =
    leg1.interestType === TradeInterestType.Floating &&
    leg2.interestType === TradeInterestType.Floating;
  if (isBothFloating) return isCrossCurrency ? SwapType.XBS : SwapType.IBS;
  if (isCrossCurrency) return SwapType.XCS;
  return SwapType.IRS;
}

const DAYS_PER_UNIT: Record<string, number> = { D: 1, W: 7, M: 30, Y: 365 };

// Rough length of a frequency in days, only for comparing two legs. Zero is the longest.
export function returnFrequencyDays(frequency: string): number {
  const match = /^(\d+)([DWMY])$/.exec(frequency);
  if (!match) return Number.POSITIVE_INFINITY;
  return Number(match[1]) * DAYS_PER_UNIT[match[2]];
}

function returnLegFrequencyDays(leg: ISwapTypeLeg): number {
  return returnFrequencyDays(leg.resetFrequency || leg.couponFrequency);
}

const USD = "USD";

// LQC-2967 - which legs take a spread. IBS: the shorter leg only (6s3s on 3M), Leg 2 on
// a tie (LEG_CREATION_PLAN Q8). XBS: both, or only the non-USD leg when one is USD.
// Any other swap: every floating leg, as before. Fixed legs never take one.
export function returnSpreadLegs(
  leg1: ISwapTypeLeg,
  leg2: ISwapTypeLeg,
): { leg1: boolean; leg2: boolean } {
  const swapType = returnSwapType(leg1, leg2);
  if (swapType === SwapType.IBS) {
    const isLeg1Shorter = returnLegFrequencyDays(leg1) < returnLegFrequencyDays(leg2);
    return { leg1: isLeg1Shorter, leg2: !isLeg1Shorter };
  }
  if (swapType === SwapType.XBS) {
    const hasUsdLeg = leg1.currency === USD || leg2.currency === USD;
    return {
      leg1: !hasUsdLeg || leg1.currency !== USD,
      leg2: !hasUsdLeg || leg2.currency !== USD,
    };
  }
  return {
    leg1: leg1.interestType === TradeInterestType.Floating,
    leg2: leg2.interestType === TradeInterestType.Floating,
  };
}

// Legs in different currencies carry different notionals, so Leg 2 is captured
// (LQC-2971). XBS included on the spec's "XCS or XBS" (LEG_CREATION_PLAN Q2).
export function isPerLegNominalSwap(swapType: SwapType): boolean {
  return swapType === SwapType.XCS || swapType === SwapType.XBS;
}
